// components/ContextualHelp.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import styles from './ContextualHelp.module.css';

interface ContextualHelpProps {
  featureArea: string;  // e.g., "medical-sos", "missions", "events"
  buttonText?: string;
}

interface HelpArticle {
  id: number;
  slug: string;
  title: string;
  category: string;
  author: {
    id: number;
    username: string;
  };
  updatedAt: string;
}

const ContextualHelp: React.FC<ContextualHelpProps> = ({ featureArea, buttonText = "?" }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [articles, setArticles] = useState<HelpArticle[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Display-friendly names for feature areas
  const featureAreaNames: Record<string, string> = {
    'medical-sos': 'Medical SOS',
    'missions': 'Missions',
    'events': 'Events',
    'departments': 'Departments',
    'positions': 'Position Applications',
    'knowledge-base': 'Knowledge Base',
    'feedback': 'Feedback System',
    'org-points': 'Organization Points'
  };

  useEffect(() => {
    if (isOpen && articles.length === 0 && !loading) {
      fetchHelp();
    }
  }, [isOpen]);

  const fetchHelp = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`/knowledgebase/contextual-help/${featureArea}`);
      setArticles(response.data);
    } catch (err: any) {
      console.error('Error fetching contextual help:', err);
      setError(err.response?.data?.message || 'No help articles available for this feature');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        className={styles.helpButton}
        onClick={() => setIsOpen(true)}
        title="Get help with this feature"
        aria-label="Help"
      >
        {buttonText}
      </button>

      {isOpen && (
        <div className={styles.modalOverlay} onClick={() => setIsOpen(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2>Help: {featureAreaNames[featureArea] || featureArea.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</h2>
              <button className={styles.closeButton} onClick={() => setIsOpen(false)}>
                ×
              </button>
            </div>
            
            <div className={styles.modalBody}>
              {loading && <p>Loading help...</p>}
              
              {error && (
                <div className={styles.errorMessage}>
                  <p>{error}</p>
                </div>
              )}
              
              {articles.length > 0 && (
                <>
                  <p className={styles.helpDescription}>
                    The following articles can help you with this feature:
                  </p>
                  <div className={styles.articleList}>
                    {articles.map(article => (
                      <div key={article.id} className={styles.articleItem}>
                        <button
                          onClick={() => {
                            setIsOpen(false);
                            navigate(`/knowledge/${article.slug}`);
                          }}
                          className={styles.articleLink}
                        >
                          <h3 className={styles.articleTitle}>{article.title}</h3>
                          <div className={styles.articleMeta}>
                            <span className={styles.category}>{article.category}</span>
                            <span className={styles.author}>by {article.author.username}</span>
                          </div>
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ContextualHelp;
